import { toast } from 'react-toastify';

import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { InstanceLink } from '~/components/Details/InstanceDetails/MenuInstances';
import api from '~/services/api';
import history from '~/services/history';
import { IState } from '~/store';

import {
  submitModalFailure,
  submitModalRequest,
  submitModalSuccess,
} from '../modal/actions';
import {
  getTasksSuccess,
  getTasksFailure,
  getTasksHistoryAllSuccess,
  getTasksHistoryAllFailure,
  getTasksHistoryCountSuccess,
  getTasksHistoryCountFailure,
  unclaimTaskSuccess,
  unclaimTaskFailure,
  getTaskSuccess,
  getTaskFailure,
  getTaskFormSuccess,
  getTaskFormFailure,
  getTaskFormVariablesSuccess,
  getTaskFormVariablesFailure,
  executeTaskSuccess,
  executeTaskFailure,
  getHistoryTaskSuccess,
  getHistoryTaskFailure,
  getTaskDataChartSuccess,
  getTaskDataChartFailure,
  getTasksStatusSuccess,
  getTasksStatusFailure,
  getTaskFormHTMLSuccess,
  getTaskFormHTMLFailure,
  getCompletedTasksSuccess,
  getCompletedTasksFailure,
  getDeletedTasksFailure,
  getDeletedTasksSuccess,
  getCancelingReasonsFailure,
  getCancelingReasonsSuccess,
  getTaskRankingSuccess,
  getTaskRankingFailure,
  getTasksRequest,
  claimTaskSuccess,
  claimTaskFailure,
  getTaskListByProcessInstanceSuccess,
  getTaskListByProcessInstanceFailure,
} from './actions';
import {
  IGetCompletedTasksRequestAction,
  IGetTasksHistoryAllRequestAction,
  IUnclaimTaskRequestAction,
  IGetTaskRequestAction,
  IGetTaskFormRequestAction,
  IExecuteTaskRequestAction,
  ITaskTypes,
  IGetHistoryTaskRequestAction,
  IGetTasksStatusRequestAction,
  TaskStatusName,
  TaskColor,
  IGetTaskFormHTMLRequestAction,
  IGetTaskFormVariablesRequestAction,
  IGetTaskRankingRequestAction,
  IGetTaskDataChartRequestAction,
  IGetTasksRequestAction,
  IClaimTaskRequestAction,
  IGetTaskListByProcessInstanceRequestAction,
} from './types';

export function* getTasks({ payload }: IGetTasksRequestAction): any {
  const { params, user } = payload;

  if (user && params.unassigned) {
    const candidateGroups = Object.entries(user.groups).map(([, value]) => {
      return value.id;
    });
    if (!params.candidateGroups) {
      params.candidateGroups = candidateGroups;
    }
  }
  try {
    const response = yield call(api.post, '/tasks', { params });

    const { tasks, count } = response.data;
    yield put(getTasksSuccess(params.sortOrder, tasks, count));
  } catch (err) {
    yield put(getTasksFailure());
    toast.error('Erro ao tentar listar tarefas disponíveis.');
  }
}

export function* getCompletedTasks({
  payload,
}: IGetCompletedTasksRequestAction): any {
  const { params, changeOrder } = payload;
  const sortOrder = yield select((state: IState) => state.tasks.sortOrder);

  if (changeOrder) {
    params.sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
  } else {
    params.sortOrder = sortOrder;
  }

  params.finished = true;

  try {
    const response = yield call(api.post, '/history/tasks', { params });

    const { tasks, total } = response.data;

    yield put(getCompletedTasksSuccess(params.sortOrder, tasks, total));
  } catch (err) {
    yield put(getCompletedTasksFailure());
    toast.error('Erro ao tentar listar tarefas concluídas por você.');
  }
}

export function* getDeletedTasks({
  payload,
}: IGetCompletedTasksRequestAction): any {
  const { params, changeOrder } = payload;
  const sortOrder = yield select((state: IState) => state.tasks.sortOrder);

  if (changeOrder) {
    params.sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
  } else {
    params.sortOrder = sortOrder;
  }

  params.finished = true;

  try {
    const response = yield call(api.post, '/history/tasks', { params });

    const { tasks, total } = response.data;

    yield put(getDeletedTasksSuccess(params.sortOrder, tasks, total));
  } catch (err) {
    yield put(getDeletedTasksFailure());
    toast.error('Erro ao tentar listar tarefas concluídas por você.');
  }
}

export function* getTasksHistoryAll({
  payload,
}: IGetTasksHistoryAllRequestAction): any {
  const { params, changeOrder } = payload;
  const sortOrder = yield select((state: IState) => state.tasks.sortOrder);

  if (changeOrder) {
    params.sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
  } else {
    params.sortOrder = sortOrder;
  }

  params.finished = true;

  try {
    const response = yield call(api.get, '/history/tasks', { params });

    const { tasks, total } = response.data;

    yield put(getTasksHistoryAllSuccess(params.sortOrder, tasks, total));
  } catch (err) {
    yield put(getTasksHistoryAllFailure());
    toast.error('Erro ao tentar listar todas tarefas concluídas.');
  }
}

export function* getTasksHistoryCount(): any {
  const params = {
    taskDeleteReason: ['completed', 'deleted'],
  };

  try {
    const response = yield call(api.get, 'history/tasks/count', { params });

    const { total } = response.headers;

    yield put(getTasksHistoryCountSuccess(total));
  } catch (err) {
    yield put(getTasksHistoryCountFailure());
    toast.error('Erro ao tentar contar tarefas concluídas.');
  }
}

export function* unclaimTask({ payload }: IUnclaimTaskRequestAction) {
  const { taskIds, getTasksParams } = payload;
  const { params, user } = getTasksParams;

  try {
    yield put(submitModalRequest());
    yield call(api.post, `/tasks/unclaim`, { taskIds });

    yield put(unclaimTaskSuccess());
    yield put(getTasksRequest(params, user));
    yield put(submitModalSuccess());
  } catch (err) {
    const error = err as any;
    yield put(unclaimTaskFailure(error.response.data.failures));
    toast.error('Erro ao tentar desatribuir a tarefa.');
  }
}

export function* getTask({ payload }: IGetTaskRequestAction): any {
  const { id, executeTask } = payload;

  try {
    const response = yield call(api.get, `/tasks/${id}`);

    yield put(getTaskSuccess(response.data, executeTask));
  } catch (err) {
    const error = err as any;
    yield put(getTaskFailure(error.response.status));
    if (error.response.status === 500) {
      toast.error('Erro ao buscar tarefa. Contate o admistrador.');
    }
  }
}

export function* claimTask({ payload }: IClaimTaskRequestAction): any {
  const { taskIds, userId, managerId, reassignee, getTasksParams } = payload;
  const { params, user } = getTasksParams;

  try {
    yield put(submitModalRequest());
    yield call(api.post, `/tasks/assignee`, {
      userId,
      taskIds,
      managerId,
      reassignee,
    });

    yield put(claimTaskSuccess());
    yield put(getTasksRequest(params, user));
    yield put(submitModalSuccess());
  } catch (err) {
    const error = err as any;
    yield put(claimTaskFailure(error.response.data.failures));
    yield put(submitModalFailure());
    toast.error('Erro ao tentar atribuir tarefa.');
  }
}

export function* getTaskForm({ payload }: IGetTaskFormRequestAction): any {
  const { task } = payload;
  const {
    id,
    processDefinitionKey: key,
    tenantId: tenant_id,
    taskDefinitionKey: taskKey,
    processInstanceId,
    processDefinitionId,
  } = task;

  try {
    const response = yield call(api.get, '/processes-form', {
      params: {
        key,
        tenant_id,
        taskKey,
        processInstanceId,
        processDefinitionId,
      },
    });

    const form = response.data;
    yield put(getTaskFormSuccess(id, form));
  } catch (err) {
    yield put(getTaskFormFailure(id));
    toast.error('Essa tarefa não possui formulário. Contate o suporte.');
  }
}

export function* getTaskFormHTML({
  payload,
}: IGetTaskFormHTMLRequestAction): any {
  const { id } = payload;

  try {
    const response = yield call(api.get, `/tasks/${id}/form`);

    const form = response.data;

    yield put(getTaskFormHTMLSuccess(id, form));
  } catch (err) {
    yield put(getTaskFormHTMLFailure(id));
  }
}

export function* getTaskFormVariables({
  payload,
}: IGetTaskFormVariablesRequestAction): any {
  const { id } = payload;

  try {
    const response = yield call(api.get, `/tasks/${id}/form-variables`);

    const formVariables = response.data;

    yield put(getTaskFormVariablesSuccess(id, formVariables));
  } catch (err) {
    yield put(getTaskFormVariablesFailure(id));
  }
}

export function* executeTask({ payload }: IExecuteTaskRequestAction): any {
  const {
    id,
    params,
    user,
    variables,
    processFiles,
    rootProcessInstanceId,
  } = payload;

  try {
    const response = yield call(api.get, `/tasks/${id}`);

    const task = response.data;

    if (task.assignee !== String(user.personId)) {
      yield put(executeTaskFailure());
      toast.error(
        'Erro ao tentar executar a tarefa. Tarefa já executada ou atribuída para outro usuário ',
      );

      setTimeout(() => {
        history.push('/tasks/atribuidas');
      }, 2000);
    } else {
      yield call(api.post, `/tasks/${id}/execute`, {
        variables,
        userId: user.personId,
      });

      yield call(api.post, 'processFiles', {
        processFiles,
      });

      if (variables?.dynamicTable) {
        yield call(api.put, '/dynamic-table-values', {
          rootProcessInstanceId,
          orgId: user.orgId,
          values: variables.dynamicTable.value,
        });
      }

      yield put(executeTaskSuccess(id));

      yield put(getTasksRequest(params, user));
      history.push('/tasks/atribuidas');
      toast.success('Tarefa executada com sucesso.');
    }
  } catch (err) {
    yield put(executeTaskFailure());
    toast.error('Erro ao tentar executar a tarefa.');
  }
}

export function* getHistoryTask({
  payload,
}: IGetHistoryTaskRequestAction): any {
  const { id } = payload;

  try {
    const response = yield call(api.get, `/history/tasks/${id}`);

    yield put(getHistoryTaskSuccess(response.data));
  } catch (err) {
    yield put(getHistoryTaskFailure());
  }
}

export function* getTotalTasksStatus({
  payload,
}: IGetTasksStatusRequestAction): any {
  const { params } = payload;

  try {
    const response = yield call(api.get, '/tasks/count-status', {
      params,
    });

    const { delayed, expiring, onTime } = response.data;
    const tasksStatusCount = [];
    tasksStatusCount.push(
      buildTaskDataChart(
        TaskStatusName.TASKS_DELAYED,
        Number(delayed),
        TaskColor.RED,
      ),
      buildTaskDataChart(
        TaskStatusName.TASKS_EXPIRING,
        Number(expiring),
        TaskColor.ORANGE,
      ),
      buildTaskDataChart(
        TaskStatusName.TASKS_ON_TIME,
        Number(onTime),
        TaskColor.GREY,
      ),
    );

    yield put(getTasksStatusSuccess(tasksStatusCount));
  } catch (err) {
    yield put(getTasksStatusFailure());
  }
}

function* getTotalTasksByStatus(params: any, url: string): any {
  const response = yield call(api.get, url, {
    params,
  });
  return response.headers.total;
}

function buildTaskDataChart(name: string, value: number, color: string) {
  return { name, value, color };
}

export function* getCancelingReasonsRequest(): any {
  try {
    const response = yield call(
      api.get,
      `/data-lists/wedo_justificativaDoCancelamento/metadata/list`,
    );

    const { data } = response.data;

    yield put(getCancelingReasonsSuccess(data));
  } catch (err) {
    yield put(getCancelingReasonsFailure());
    toast.error('Houve um problema ao buscar os motivos de cancelamento.');
  }
}

export function* getAllTasksCount({
  payload,
}: IGetTaskDataChartRequestAction): any {
  const { params } = payload;

  try {
    const response = yield all([
      getTotalTasksByStatus(
        {
          assigneeIn: params?.taskAssignee,
          taskDeleteReason: 'completed',
          endTime: params?.dateRange,
        },
        'history/tasks/count',
      ),
      getTotalTasksByStatus(
        {
          assigneeIn: params?.taskAssignee,
          taskDeleteReason: 'deleted',
          endTime: params?.dateRange,
        },
        'history/tasks/count',
      ),
    ]);
    const [totalCompleted, totalCanceled] = response;

    const taskDataCharts = [];
    taskDataCharts.push(
      buildTaskDataChart(
        TaskStatusName.TASKS_COMPLETED,
        Number(totalCompleted),
        TaskColor.DARK_RED,
      ),
      buildTaskDataChart(
        TaskStatusName.TASKS_CANCELED,
        Number(totalCanceled),
        TaskColor.ROSE,
      ),
    );

    yield put(getTaskDataChartSuccess(taskDataCharts));
  } catch (err) {
    yield put(getTaskDataChartFailure());
    toast.error('Erro ao tentar construir gráfico de tarefas.');
  }
}

export function* getTaskRanking({
  payload,
}: IGetTaskRankingRequestAction): any {
  const { params } = payload;

  try {
    const response = yield call(api.get, `history/tasks/ranking`, { params });

    yield put(getTaskRankingSuccess(response.data));
  } catch (err) {
    yield put(getTaskRankingFailure());
    toast.error('Erro ao tentar construir o ranking de tarefas.');
  }
}

export function* getTaskListByProcessInstance({
  payload,
}: IGetTaskListByProcessInstanceRequestAction): any {
  const { processInstanceId } = payload;

  try {
    const response = yield call(
      api.get,
      `/tasks/process/${processInstanceId}/tasks`,
    );
    const tasks = response.data as Array<InstanceLink>;

    yield put(getTaskListByProcessInstanceSuccess(tasks));
  } catch (err) {
    yield put(getTaskListByProcessInstanceFailure());
    toast.error('Erro ao buscar lista de tarefas');
  }
}

export default all([
  takeLatest(ITaskTypes.GET_TASKS_REQUEST, getTasks),
  takeLatest(ITaskTypes.GET_COMPLETED_TASKS_REQUEST, getCompletedTasks),
  takeLatest(ITaskTypes.GET_DELETED_TASKS_REQUEST, getDeletedTasks),
  takeLatest(ITaskTypes.GET_TASKS_HISTORY_ALL_REQUEST, getTasksHistoryAll),
  takeLatest(ITaskTypes.GET_TASKS_HISTORY_COUNT_REQUEST, getTasksHistoryCount),
  takeLatest(ITaskTypes.GET_TASK_REQUEST, getTask),
  takeLatest(ITaskTypes.CLAIM_TASK_REQUEST, claimTask),
  takeLatest(ITaskTypes.UNCLAIM_TASK_REQUEST, unclaimTask),
  takeLatest(ITaskTypes.GET_TASK_FORM_REQUEST, getTaskForm),
  takeLatest(ITaskTypes.GET_TASK_FORM_HTML_REQUEST, getTaskFormHTML),
  takeLatest(ITaskTypes.GET_TASK_FORM_VARIABLES_REQUEST, getTaskFormVariables),
  takeLatest(ITaskTypes.EXECUTE_TASK_REQUEST, executeTask),
  takeLatest(ITaskTypes.GET_HISTORY_TASK_REQUEST, getHistoryTask),
  takeLatest(ITaskTypes.GET_TASK_DATA_CHART_REQUEST, getAllTasksCount),
  takeLatest(ITaskTypes.GET_TASKS_STATUS_REQUEST, getTotalTasksStatus),
  takeLatest(
    ITaskTypes.GET_CANCELING_REASONS_REQUEST,
    getCancelingReasonsRequest,
  ),
  takeLatest(ITaskTypes.GET_TASK_RANKING_REQUEST, getTaskRanking),
  takeLatest(
    ITaskTypes.GET_TASK_LIST_BY_PROCESS_INSTANCE_REQUEST,
    getTaskListByProcessInstance,
  ),
]);
